import React from 'react';
import { Poppins, StyledAvatar, TextOverflowTooltip } from 'src/common';
import useWorkspaceSwitch from 'src/hooks/useWorkspaceSwitch';
import { useStateSelector } from 'src/redux';
import { getProfileCredentials } from 'src/utils/auth';
import { getAvatarColor } from 'src/utils/avatarColor';
import styled from 'styled-components';

const Div = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .padlock {
    position: absolute;
    transform: translateX(-26px);
  }
  .content {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    max-width: calc(100% - 30px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;

    span {
      font-weight: 500;
    }
  }
`;

interface OrgAvatarProps {
  name: string;
  imgUrl?: string | null;
  gradientBlue?: boolean;
  padlock?: boolean;
  canAccess?: boolean;
  id: string;
  /** Tree nesting level (1-based). Used to determine avatar color. */
  level?: number;
  /** Whether this is a workspace (W) or organisation (O) */
  isWorkspace?: boolean;
}

export const OrgAvatar: React.FC<OrgAvatarProps> = ({
  name,
  imgUrl,
  gradientBlue,
  padlock,
  canAccess,
  id,
  level,
  isWorkspace,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);

  const { switchOrg, isLoading: isLoadingSwitch } = useWorkspaceSwitch();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canAccess && !isLoadingSwitch) {
      switchOrg(id);
    }
  };

  // Compute avatar color based on level and type
  // Only 'Cydea' (by name) uses prussianBlue at level 1
  // Falls back to gradientBlue prop if new colors aren't available
  const avatarBgColor =
    level !== undefined && isWorkspace !== undefined
      ? getAvatarColor({ colors, isWorkspace, level, name })
      : undefined;

  // Use gradientBlue as fallback when avatarBgColor is not set (for backwards compatibility)
  const useGradientBlue = !avatarBgColor && (gradientBlue ?? isWorkspace);

  return (
    <Div>
      {padlock && (
        <div className="padlock">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="19" viewBox="0 0 14 19" fill="none">
            <path
              d="M12.5854 8.16754H11.4017V5.6423C11.4017 3.19599 9.3894 1.1837 6.94308 1.1837C4.49677 1.1837 2.48448 3.19599 2.48448 5.6423V8.16754H1.30078V5.6423C1.30078 2.52522 3.826 0 6.94308 0C10.0602 0 12.5854 2.52522 12.5854 5.6423V8.16754Z"
              fill={colors.cflowerBlue}
            />
            <path
              d="M11.9948 18.9006H1.89393C0.868055 18.9006 0 18.0326 0 17.0067V9.43103C0 8.40515 0.868055 7.53711 1.89393 7.53711H11.9948C13.0207 7.53711 13.8888 8.40515 13.8888 9.43103V17.0067C13.8888 18.072 13.0207 18.9006 11.9948 18.9006ZM1.89393 8.79972C1.53882 8.79972 1.26262 9.07592 1.26262 9.43103V17.0067C1.26262 17.3618 1.53882 17.638 1.89393 17.638H11.9948C12.3499 17.638 12.6261 17.3618 12.6261 17.0067V9.43103C12.6261 9.07592 12.3499 8.79972 11.9948 8.79972H1.89393Z"
              fill={colors.cflowerBlue}
            />
            <path
              d="M8.20687 11.3232C8.20687 12.0334 7.65447 12.5858 6.94425 12.5858C6.23403 12.5858 5.68164 12.0334 5.68164 11.3232C5.68164 10.6129 6.23403 10.0605 6.94425 10.0605C7.65447 10.0605 8.20687 10.6524 8.20687 11.3232Z"
              fill={colors.cflowerBlue}
            />
            <path d="M7.25871 11.3242H6.6274L5.99609 15.1121H7.89001L7.25871 11.3242Z" fill={colors.cflowerBlue} />
          </svg>
        </div>
      )}
      <div className="content">
        <StyledAvatar $small $xsmall src={imgUrl || ''} $gradientBlue={useGradientBlue} $bgColor={avatarBgColor}>
          {getProfileCredentials(name)}
        </StyledAvatar>
        <TextOverflowTooltip>
          <Poppins
            weight={500}
            px={14}
            style={{ opacity: isLoadingSwitch ? 0.7 : 1 }}
            className={canAccess ? 'table-link' : ''}
            onClick={handleClick}
          >
            {name}
          </Poppins>
        </TextOverflowTooltip>
      </div>
    </Div>
  );
};
